import React, { useState, useEffect, Fragment, useCallback } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { useModuleUpdate } from "../moduleServices";
import { useCreateActiveModule } from "../../../manage-organizations/organizationServices";
import { useDeleteActiveModule } from "../../../manage-organizations/organizationServices";
import { isActive } from "../util";
import { equals, pluck } from "ramda";
import { useSnackAlert } from "../../../common/use-snackAlert";
import { useCurrentOrgId, useIsAdmin } from "../../../auth/authUtils";
import { allTrue, notBlank } from "../../../util/ext";

import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";

import PublishStatusDialog from "./PublishStatusDialog";
import StatusBadge from "../../common/StatusBadge";
import { useMediaPermissions } from "../../../util/mediaUtil";

const useStyles = makeStyles(theme => ({
  form: {
    marginRight: theme.spacing(2),
  },

  unpublished: {
    fontStyle: "italic",
    fontWeight: 700,
    color: theme.palette.secondary.contrastText,
  },

  published: {
    fontWeight: 700,
    color: theme.palette.success.main,
  },

  deleted: {
    fontWeight: 700,
    color: theme.palette.deleted,
  },
}));

const getLabel = status => {
  switch (status) {
    case "ACTIVE":
      return "Live";
    case "INACTIVE":
      return "Draft";
    case "DELETED":
      return "Deleted";
    default:
      return "";
  }
};

const PublishStatusSwitch = props => {
  const classes = useStyles();
  const { view, module } = props;

  const isAdmin = useIsAdmin();
  const currentOrgId = useCurrentOrgId();
  const { readOnly } = useMediaPermissions(module);
  const headerView = equals(view, "header");
  const tableView = equals(view, "table");
  const [switchLabel, setSwitchLabel] = useState("");
  const [dialogAction, setDialogAction] = useState("");

  const { SnackAlert, showAlert } = useSnackAlert();

  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    setLoading(true);
    setSwitchLabel(getLabel(module.status));

    if (isActive(module.status)) {
      setChecked(true);
      setDialogAction("unpublish");
    } else {
      setChecked(false);
      setDialogAction("publish");
    }
    setLoading(false);
  }, [loading, module.status]);

  const updateModule = useModuleUpdate();
  const createActiveModule = useCreateActiveModule();
  const deleteActiveModule = useDeleteActiveModule();
  const changeStatus = useCallback(async () => {
    try {
      setOpen(false);
      const newStatus = isActive(module.status) ? "INACTIVE" : "ACTIVE";

      const variables = {
        resourceIds: {
          moduleId: module.id,
          orgId: currentOrgId,
        },
      };

      if (isActive(newStatus)) {
        await createActiveModule.mutateAsync(variables);
      } else {
        await deleteActiveModule.mutateAsync(variables);
      }

      await updateModule.mutateAsync({
        resourceIds: { id: module.id },
        data: {
          status: newStatus,
        },
      });
    } catch (err) {
      const messages = notBlank(err.body.errors)
        ? pluck("message", err.body.errors)
        : [
            "There was an issue changing the status of this module. Please try again later.",
          ];
      showAlert("error", messages);
    }
  }, [
    currentOrgId,
    createActiveModule,
    deleteActiveModule,
    updateModule,
    module,
    showAlert,
  ]);

  //-- Handle publish status dialog
  const [open, setOpen] = useState(false);
  const openDialog = () => setOpen(true);
  const closeDialog = () => setOpen(false);

  return (
    <Fragment>
      <SnackAlert />

      <PublishStatusDialog
        open={open}
        action={dialogAction}
        handleClickClose={closeDialog}
        handleStatusChange={changeStatus}
      />

      {allTrue([!isAdmin, !readOnly]) ? (
        <FormControl classes={{ root: classes.form }}>
          <FormControlLabel
            classes={{
              label: checked ? classes.published : classes.unpublished,
            }}
            control={
              <Switch onClick={openDialog} color="primary" checked={checked} />
            }
            label={
              headerView && (
                <StatusBadge
                  status={module.status}
                  label={switchLabel}
                  size="sm"
                />
              )
            }
            labelPlacement={tableView ? "end" : "start"}
          />
        </FormControl>
      ) : (
        <StatusBadge
          status={module.status}
          label={switchLabel}
          size="sm"
          labelOnly={readOnly}
        />
      )}
    </Fragment>
  );
};

export default PublishStatusSwitch;
